/**
 * Логика формы заявки на подбор (ТЗ-2026-011), вынесенная из processes.tsx для тестов.
 *
 * recPatch — единственная точка изменения стейта формы: применяет патч и приводит парные
 * поля к инвариантам видимости (аудит §4.1). Раньше `up()` только мержил, и скрытое условием
 * поле оставалось в стейте → в БД оседали противоречивые анкеты («бюджетированная вакансия,
 * вид штатной единицы — стажёр, срок 6 мес.»). Инварианты зеркалят условия рендера секций.
 */
import { REC_REPLACE_REASONS } from '../../lib/edo-labels'
import type { RecruitmentInput } from '../../lib/types'

export function recPatch(f: RecruitmentInput, patch: Partial<RecruitmentInput>): RecruitmentInput {
  const n: RecruitmentInput = { ...f, ...patch }
  // Вид штатной единицы и срок — только у новой штатной единицы.
  if (n.vacancyType !== 'new') {
    n.staffUnitType = undefined
    n.termOfValidity = undefined
  }
  // Срок действия — только у временной/проектной единицы.
  if (n.staffUnitType !== 'temporary' && n.staffUnitType !== 'project') n.termOfValidity = undefined
  // Категория ВУ — только при отмеченном водительском удостоверении.
  if (!n.driverLicense) n.driverCategory = undefined
  // Виды проверки — только при требуемом тестировании.
  if (!n.testingRequired) n.testTypes = undefined
  // «Кого замещает» — только при причинах увольнения/изменения подразделения.
  if (!(n.reasons ?? []).some((r) => REC_REPLACE_REASONS.includes(r))) n.replacesName = undefined
  return n
}

export type RecruitmentMissing =
  | 'jobTitle' | 'jobGrade' | 'department' | 'workLocation' | 'vacancyType' | 'reasons'
  | 'replacesName' | 'plannedStartDate' | 'experience' | 'education' | 'techSkills'

/**
 * Пустые обязательные поля анкеты (ТЗ §6.5 / КП-01) — все разом и в порядке экрана, для подсказки
 * под неактивной кнопкой. Ошибки значения («от» > «до», компетенции короче 50 символов) сюда не
 * входят: у них своя красная подсказка в форме.
 */
export function recruitmentMissing(f: RecruitmentInput): RecruitmentMissing[] {
  const needReplace = (f.reasons ?? []).some((r) => REC_REPLACE_REASONS.includes(r))
  const missing: (RecruitmentMissing | false)[] = [
    !f.jobTitle.trim() && 'jobTitle',
    !f.jobGradeCode && 'jobGrade',
    !f.department.trim() && 'department',
    !f.workLocation?.trim() && 'workLocation',
    !f.vacancyType && 'vacancyType',
    !f.reasons?.length && 'reasons',
    needReplace && !f.replacesName?.trim() && 'replacesName',
    !f.plannedStartDate && 'plannedStartDate',
    !f.experience && 'experience',
    !f.education && 'education',
    !f.techSkills?.trim() && 'techSkills',
  ]
  return missing.filter((x): x is RecruitmentMissing => !!x)
}
